import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaCheckCircle, FaClock, FaGripVertical, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";

import MenuLateral from "../../Componentes/MenuLateral";
import MicroPlanningPanel from "./MicroPlanningPanel";
import OrderSelectionPanel from "./OrderSelectionPanel";
import {
  normalizeCityIdentity,
  normalizeCityValue,
} from "./cityNormalization";
import { projectApi, routingApi } from "../../Services/api";
import {
  buildRoutingRequestKey,
  formatRoutingCooldown,
  getRoutingErrorFeedback,
  ROUTING_PROVIDER_UNAUTHORIZED_CODE,
} from "./routingApiFeedback";

import "./index.css";

const DEFAULT_CENTER = [-15.2436862, -53.6479108];
const DEFAULT_ROUTE_CITY = "Passos - MG";
const ROUTING_TOKEN_KEY = "routingToken";
const ROUTING_COOLDOWN_STORAGE_KEY = "palmaresRoutingCooldown";
const ROUTING_AUTH_PATH =
  process.env.REACT_APP_ROUTING_API_AUTH_PATH || "/auth/signin";
const ROUTING_CLIENT_EMAIL = process.env.REACT_APP_ROUTING_API_EMAIL || "";
const ROUTING_CLIENT_PASSWORD =
  process.env.REACT_APP_ROUTING_API_PASSWORD || "";
const IBGE_MUNICIPIOS_ENDPOINT =
  "https://servicodados.ibge.gov.br/api/v1/localidades/municipios";

function readStoredRoutingCooldown(now = Date.now()) {
  try {
    const storedValue = localStorage.getItem(ROUTING_COOLDOWN_STORAGE_KEY);

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);
    const retryAt = Number(parsedValue?.retryAt);

    if (!Number.isFinite(retryAt) || retryAt <= now) {
      localStorage.removeItem(ROUTING_COOLDOWN_STORAGE_KEY);
      return null;
    }

    return {
      code: parsedValue?.code || null,
      message:
        parsedValue?.message ||
        "O serviço de roteamento está temporariamente indisponível.",
      retryAt,
    };
  } catch {
    localStorage.removeItem(ROUTING_COOLDOWN_STORAGE_KEY);
    return null;
  }
}

function isValidLatLng(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function hasValidCoordinates(point) {
  return isValidLatLng(point?.coordinates?.lat, point?.coordinates?.lng);
}

function RouteMapController({ focusTarget }) {
  const map = useMap();

  useEffect(() => {
    const points = Array.isArray(focusTarget?.points) ? focusTarget.points : [];
    const validPoints = points.filter(
      (point) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        isValidLatLng(point[0], point[1])
    );

    if (validPoints.length === 0) {
      return;
    }

    map.invalidateSize();

    if (validPoints.length === 1) {
      map.setView(validPoints[0], focusTarget?.zoom || 13, {
        animate: true,
      });
      return;
    }

    map.fitBounds(L.latLngBounds(validPoints), {
      animate: true,
      padding: [42, 42],
      maxZoom: focusTarget?.maxZoom || 15,
    });
  }, [focusTarget, map]);

  return null;
}

const Roteamento = () => {
  const navigate = useNavigate();

  const [origin, setOrigin] = useState(DEFAULT_ROUTE_CITY);
  const [destination, setDestination] = useState(DEFAULT_ROUTE_CITY);
  const [cityInput, setCityInput] = useState("");
  const [waypoints, setWaypoints] = useState([]);
  const [routePoints, setRoutePoints] = useState([]);
  const [routeSegments, setRouteSegments] = useState([]);
  const [routePolyline, setRoutePolyline] = useState([]);
  const [routeOptimizationData, setRouteOptimizationData] = useState(null);
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedWaypointId, setDraggedWaypointId] = useState(null);
  const [dragOverWaypointId, setDragOverWaypointId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [routeName, setRouteName] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedRouteStatus, setSelectedRouteStatus] = useState(0);
  const [savingRoute, setSavingRoute] = useState(false);
  const [exportingRouteJson, setExportingRouteJson] = useState(false);
  const [lastFinalizedLotId, setLastFinalizedLotId] = useState(null);
  const [lastFinalizedOperationalRouteId, setLastFinalizedOperationalRouteId] =
    useState(null);
  const [operationalRouteId, setOperationalRouteId] = useState(null);
  const [operationalRouteSource, setOperationalRouteSource] = useState(null);
  const [openingRoute, setOpeningRoute] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showAvailableOrderRoutesModal, setShowAvailableOrderRoutesModal] =
    useState(false);
  const [availableOrderRoutes, setAvailableOrderRoutes] = useState([]);
  const [loadingAvailableOrderRoutes, setLoadingAvailableOrderRoutes] =
    useState(false);
  const [availableOrderRoutesError, setAvailableOrderRoutesError] = useState("");
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedWaypointForOrder, setSelectedWaypointForOrder] = useState(null);
  const [orderInput, setOrderInput] = useState("");
  const [modalStatusFilter, setModalStatusFilter] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [cityCatalog, setCityCatalog] = useState([]);
  const [loadingCityCatalog, setLoadingCityCatalog] = useState(false);
  const [cityCatalogError, setCityCatalogError] = useState("");
  const [macroStopsPayload, setMacroStopsPayload] = useState(null);
  const [loadingMacroStops, setLoadingMacroStops] = useState(false);
  const [macroStopsError, setMacroStopsError] = useState("");
  const [microPanelOpen, setMicroPanelOpen] = useState(false);
  const [selectedMicroStopOrder, setSelectedMicroStopOrder] = useState(null);
  const [selectedMicroStop, setSelectedMicroStop] = useState(null);
  const [microMapPoints, setMicroMapPoints] = useState([]);
  const [microPlanningDirty, setMicroPlanningDirty] = useState(false);
  const [orderSelectionPayload, setOrderSelectionPayload] = useState(null);
  const [loadingOrderSelection, setLoadingOrderSelection] = useState(false);
  const [orderSelectionError, setOrderSelectionError] = useState("");
  const [orderSelectionDirty, setOrderSelectionDirty] = useState(false);
  const [mapFocusTarget, setMapFocusTarget] = useState(null);
  const [routingCooldown, setRoutingCooldown] = useState(() =>
    readStoredRoutingCooldown()
  );
  const [routingClock, setRoutingClock] = useState(Date.now());
  const routingCooldownRef = useRef(routingCooldown);
  const inFlightRoutingRequestsRef = useRef(new Map());
  const microRoutingStage2Enabled = Boolean(
    macroStopsPayload?.configuracao?.microRoteamentoEtapa2Habilitada
  );
  const isRouteReadOnly = selectedRouteStatus === 1;
  const profile = "driving-car";
  const routingCooldownRemaining = routingCooldown
    ? Math.max(0, routingCooldown.retryAt - routingClock)
    : 0;
  const isRoutingCooldownActive = routingCooldownRemaining > 0;
  const routingRetryAtLabel = routingCooldown?.retryAt
    ? new Date(routingCooldown.retryAt).toLocaleString("pt-BR")
    : "";
  const CITY_NOT_ALLOWED_MESSAGE =
    "Cidade nao permitida, nao consta no cadastro do IBGE.";

  useEffect(() => {
    if (localStorage.getItem("usuarioLogado") === null) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    routingCooldownRef.current = routingCooldown;

    if (!routingCooldown) {
      localStorage.removeItem(ROUTING_COOLDOWN_STORAGE_KEY);
      return undefined;
    }

    localStorage.setItem(
      ROUTING_COOLDOWN_STORAGE_KEY,
      JSON.stringify(routingCooldown)
    );

    const updateClock = () => {
      const now = Date.now();
      setRoutingClock(now);

      if (routingCooldown.retryAt <= now) {
        routingCooldownRef.current = null;
        localStorage.removeItem(ROUTING_COOLDOWN_STORAGE_KEY);
        setRoutingCooldown(null);
      }
    };

    updateClock();
    const intervalId = window.setInterval(updateClock, 1000);

    return () => window.clearInterval(intervalId);
  }, [routingCooldown]);

  useEffect(() => {
    let cancelled = false;

    async function loadCityCatalog() {
      setLoadingCityCatalog(true);
      setCityCatalogError("");

      try {
        const response = await fetch(IBGE_MUNICIPIOS_ENDPOINT);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data : [];

        const parsedCities = list
          .map((item) => {
            const name = item?.nome || "";
            const uf =
              item?.microrregiao?.mesorregiao?.UF?.sigla ||
              item?.regiaoImediata?.regiaoIntermediaria?.UF?.sigla ||
              "";

            if (!name || !uf) {
              return null;
            }

            return {
              id: item?.id || null,
              name,
              uf,
              label: `${name} - ${uf}`,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        if (!cancelled) {
          setCityCatalog(parsedCities);
        }
      } catch (error) {
        if (!cancelled) {
          setCityCatalog([]);
          setCityCatalogError(
            "Nao foi possivel carregar cidades do IBGE para validacao."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingCityCatalog(false);
        }
      }
    }

    loadCityCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMacroStops() {
      if (!selectedRouteId) {
        setMacroStopsPayload(null);
        setMacroStopsError("");
        setMicroPanelOpen(false);
        setSelectedMicroStopOrder(null);
        setSelectedMicroStop(null);
        setMicroMapPoints([]);
        return;
      }

      setLoadingMacroStops(true);
      setLoadingOrderSelection(true);
      setMacroStopsError("");
      setOrderSelectionError("");

      try {
        const [stopsResponse, selectionResponse] = await Promise.all([
          projectApi.get(`/roteamento/rotas/${selectedRouteId}/paradas`),
          projectApi.get(
            `/roteamento/rotas/${selectedRouteId}/selecao-pedidos`
          ),
        ]);

        if (!cancelled) {
          setMacroStopsPayload(stopsResponse?.data || null);
          setOrderSelectionPayload(selectionResponse?.data || null);
          setOrderSelectionDirty(selectionResponse?.data?.salva !== true);
        }
      } catch (error) {
        if (!cancelled) {
          setMacroStopsPayload(null);
          setOrderSelectionPayload(null);
          setMacroStopsError(
            error?.response?.data?.errors?.default ||
              "Nao foi possivel carregar as paradas para micro-roteirizacao."
          );
          setOrderSelectionError(
            error?.response?.data?.errors?.default ||
              "Nao foi possivel carregar a selecao de pedidos."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingMacroStops(false);
          setLoadingOrderSelection(false);
        }
      }
    }

    loadMacroStops();

    return () => {
      cancelled = true;
    };
  }, [selectedRouteId]);

  const mapCenter = useMemo(() => {
    if (
      routePolyline.length > 0 &&
      Array.isArray(routePolyline[0]) &&
      isValidLatLng(routePolyline[0][0], routePolyline[0][1])
    ) {
      return routePolyline[0];
    }

    if (routePoints.length > 0 && hasValidCoordinates(routePoints[0])) {
      const firstPoint = routePoints[0];
      return [firstPoint.coordinates.lat, firstPoint.coordinates.lng];
    }

    return DEFAULT_CENTER;
  }, [routePoints, routePolyline]);

  const cityResolver = useMemo(() => {
    const labelByCompositeKey = new Map();
    const labelByIdentityCompositeKey = new Map();
    const labelsByName = new Map();
    const labelsByIdentity = new Map();

    cityCatalog.forEach((city) => {
      const normalizedName = normalizeCityValue(city.name);
      const normalizedUf = normalizeCityValue(city.uf);
      const compositeKey = `${normalizedName}|${normalizedUf}`;
      const identityName = normalizeCityIdentity(city.name);
      const identityCompositeKey = `${identityName}|${normalizedUf}`;

      labelByCompositeKey.set(compositeKey, city.label);
      labelByIdentityCompositeKey.set(identityCompositeKey, city.label);

      if (!labelsByName.has(normalizedName)) {
        labelsByName.set(normalizedName, []);
      }

      labelsByName.get(normalizedName).push(city.label);

      if (!labelsByIdentity.has(identityName)) {
        labelsByIdentity.set(identityName, []);
      }

      labelsByIdentity.get(identityName).push(city.label);
    });

    function resolve(value) {
      const normalized = normalizeCityValue(value);

      if (!normalized) {
        return null;
      }

      const directMatch = cityCatalog.find(
        (city) => normalizeCityValue(city.label) === normalized
      );

      if (directMatch) {
        return directMatch.label;
      }

      const parts = normalized.split(/[-/,\s]+/).filter(Boolean);
      const maybeUf = parts.length > 0 ? parts[parts.length - 1] : "";

      if (/^[a-z]{2}$/.test(maybeUf)) {
        const namePart = parts.slice(0, -1).join(" ");
        const compositeKey = `${namePart}|${maybeUf}`;
        const byUf = labelByCompositeKey.get(compositeKey);

        if (byUf) {
          return byUf;
        }

        const byIdentityAndUf = labelByIdentityCompositeKey.get(
          `${normalizeCityIdentity(namePart)}|${maybeUf}`
        );

        if (byIdentityAndUf) {
          return byIdentityAndUf;
        }
      }

      const nameMatches = labelsByName.get(normalized) || [];

      if (nameMatches.length === 1) {
        return nameMatches[0];
      }

      const identityMatches =
        labelsByIdentity.get(normalizeCityIdentity(normalized)) || [];

      if (identityMatches.length === 1) {
        return identityMatches[0];
      }

      return null;
    }

    return {
      resolve,
      options: cityCatalog.map((city) => city.label),
    };
  }, [cityCatalog]);

  function resolveCityOrNull(value) {
    if (!value || !value.trim()) {
      return null;
    }

    return cityResolver.resolve(value);
  }

  const getRoutePointForStop = useCallback((stop) => {
    if (!stop?.nome) {
      return null;
    }

    const normalizedStopName = normalizeCityValue(stop.nome);

    return (
      routePoints.find(
        (point) => normalizeCityValue(point?.address) === normalizedStopName
      ) || null
    );
  }, [routePoints]);

  const getStopFocusPoint = useCallback((stop) => {
    const routePoint = getRoutePointForStop(stop);

    if (!hasValidCoordinates(routePoint)) {
      return null;
    }

    return [routePoint.coordinates.lat, routePoint.coordinates.lng];
  }, [getRoutePointForStop]);

  function openMicroPanelForStop(stop) {
    if (!stop?.ordem || isRouteReadOnly) {
      return;
    }

    if (
      microPlanningDirty &&
      Number(stop.ordem) !== Number(selectedMicroStopOrder) &&
      !window.confirm(
        "Existem alteracoes locais nao confirmadas. Deseja trocar de cidade e descarta-las?"
      )
    ) {
      return;
    }

    setSelectedMicroStopOrder(stop.ordem);
    setSelectedMicroStop(stop);
    setMicroPanelOpen(true);
    setMicroMapPoints([]);

    const focusPoint = microRoutingStage2Enabled
      ? getStopFocusPoint(stop)
      : null;

    if (focusPoint) {
      setMapFocusTarget({
        id: `macro-stop-${stop.ordem}-${Date.now()}`,
        points: [focusPoint],
        zoom: 12,
      });
    }
  }

  function closeMicroPanel(discardConfirmed = false) {
    if (
      !discardConfirmed &&
      microPlanningDirty &&
      !window.confirm(
        "Existem alteracoes locais nao confirmadas. Deseja fechar e descarta-las?"
      )
    ) {
      return;
    }

    setMicroPanelOpen(false);
    setSelectedMicroStopOrder(null);
    setSelectedMicroStop(null);
    setMicroMapPoints([]);
    setMicroPlanningDirty(false);
  }

  const handleMicroMapPointsChange = useCallback(
    (points) => {
      const nextPoints = Array.isArray(points) ? points : [];
      setMicroMapPoints(nextPoints);

      const focusPoints = nextPoints
        .map((point) => point.position)
        .filter(
          (position) =>
            Array.isArray(position) &&
            position.length >= 2 &&
            isValidLatLng(position[0], position[1])
        );
      const macroFocusPoint = getStopFocusPoint(selectedMicroStop);

      if (macroFocusPoint) {
        focusPoints.push(macroFocusPoint);
      }

      if (focusPoints.length > 0) {
        setMapFocusTarget({
          id: `micro-points-${selectedMicroStop?.ordem || "stop"}-${Date.now()}`,
          points: focusPoints,
          zoom: 13,
          maxZoom: 15,
        });
      }
    },
    [getStopFocusPoint, selectedMicroStop]
  );

  function showValidationAlert(message) {
    Swal.fire({
      icon: "warning",
      title: "Validacao de cidade",
      text: message,
      confirmButtonText: "OK",
    });
  }

  function createWaypoint(address, coordinates = null) {
    return {
      id: `${Date.now()}-${Math.random()}`,
      address,
      coordinates,
    };
  }

  function parseJsonRoute(jsonRoute) {
    if (!jsonRoute) {
      return null;
    }

    if (typeof jsonRoute === "string") {
      try {
        return JSON.parse(jsonRoute);
      } catch (error) {
        return null;
      }
    }

    if (typeof jsonRoute === "object") {
      return jsonRoute;
    }

    return null;
  }

  function decodeEncodedPolyline(polyline, precision = 5) {
    if (typeof polyline !== "string" || !polyline.trim()) {
      return [];
    }

    let index = 0;
    let lat = 0;
    let lng = 0;
    const factor = Math.pow(10, precision);
    const coordinates = [];

    while (index < polyline.length) {
      let result = 0;
      let shift = 0;
      let byte = null;

      do {
        if (index >= polyline.length) {
          return [];
        }
        byte = polyline.charCodeAt(index) - 63;
        index += 1;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index < polyline.length + 1);

      const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += deltaLat;

      result = 0;
      shift = 0;

      do {
        if (index >= polyline.length) {
          return [];
        }
        byte = polyline.charCodeAt(index) - 63;
        index += 1;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index < polyline.length + 1);

      const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += deltaLng;

      coordinates.push([lat / factor, lng / factor]);
    }

    return coordinates;
  }

  function extractGeometryCoordinates(rawGeometry) {
    if (!rawGeometry) {
      return {
        coordinates: [],
        format: null,
      };
    }

    if (typeof rawGeometry === "string") {
      const decodedPolyline5 = decodeEncodedPolyline(rawGeometry, 5);

      if (decodedPolyline5.length > 1) {
        return {
          coordinates: decodedPolyline5,
          format: "latlng",
        };
      }

      const decodedPolyline6 = decodeEncodedPolyline(rawGeometry, 6);

      if (decodedPolyline6.length > 1) {
        return {
          coordinates: decodedPolyline6,
          format: "latlng",
        };
      }

      return {
        coordinates: [],
        format: null,
      };
    }

    if (rawGeometry?.type === "Feature") {
      return extractGeometryCoordinates(rawGeometry.geometry);
    }

    if (rawGeometry?.type === "LineString") {
      return {
        coordinates: Array.isArray(rawGeometry.coordinates)
          ? rawGeometry.coordinates
          : [],
        format: "lnglat",
      };
    }

    if (
      rawGeometry?.geometry &&
      (rawGeometry.geometry.type || Array.isArray(rawGeometry.geometry))
    ) {
      return extractGeometryCoordinates(rawGeometry.geometry);
    }

    if (Array.isArray(rawGeometry)) {
      return {
        coordinates: rawGeometry,
        format: "lnglat",
      };
    }

    return {
      coordinates: [],
      format: null,
    };
  }

  function getRouteDateValue(item) {
    const currentDate =
      item?.dataalteracao ||
      item?.["dataalteração"] ||
      item?.datacriacao ||
      null;
    const parsed = currentDate ? new Date(currentDate).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatDateTime(value) {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("pt-BR");
  }

  function sanitizeFileName(value) {
    return String(value || "rota")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .trim()
      .replace(/\s+/g, "_");
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function downloadFile(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  function splitCityAndUf(address) {
    const normalizedAddress = String(address || "").trim();

    if (!normalizedAddress) {
      return {
        cidade: "-",
        uf: "-",
      };
    }

    const parts = normalizedAddress.split(/\s*-\s*/);

    if (parts.length >= 2) {
      const uf = parts[parts.length - 1].trim().toUpperCase();
      const cidade = parts.slice(0, -1).join(" - ").trim();

      return {
        cidade: cidade || normalizedAddress,
        uf: uf || "-",
      };
    }

    const matchingCity = cityCatalog.find((city) => city.label === normalizedAddress);

    if (matchingCity) {
      return {
        cidade: matchingCity.name || normalizedAddress,
        uf: matchingCity.uf || "-",
      };
    }

    return {
      cidade: normalizedAddress,
      uf: "-",
    };
  }

  function activateRoutingCooldown(cooldown) {
    if (!cooldown || !Number.isFinite(Number(cooldown.retryAt))) {
      return;
    }

    const normalizedCooldown = {
      code: cooldown.code || null,
      message: cooldown.message,
      retryAt: Number(cooldown.retryAt),
    };

    routingCooldownRef.current = normalizedCooldown;
    setRoutingCooldown(normalizedCooldown);
    setRoutingClock(Date.now());
    localStorage.setItem(
      ROUTING_COOLDOWN_STORAGE_KEY,
      JSON.stringify(normalizedCooldown)
    );
  }

  function clearRoutingCooldown() {
    routingCooldownRef.current = null;
    setRoutingCooldown(null);
    localStorage.removeItem(ROUTING_COOLDOWN_STORAGE_KEY);
  }

  function getActiveRoutingCooldown() {
    const currentCooldown = routingCooldownRef.current;

    if (!currentCooldown) {
      return null;
    }

    if (currentCooldown.retryAt <= Date.now()) {
      clearRoutingCooldown();
      return null;
    }

    return currentCooldown;
  }

  function createRoutingCooldownError(cooldown) {
    const error = new Error(cooldown.message);
    error.routingCooldown = cooldown;
    return error;
  }

  function handleRoutingError(error) {
    const feedback = getRoutingErrorFeedback(error);

    if (feedback.cooldown) {
      activateRoutingCooldown(feedback.cooldown);
    }

    console.error("Falha na API de roteamento.", {
      status: feedback.status,
      code: feedback.code,
      retryAt: feedback.cooldown?.retryAt || null,
      providerStatusCode: error?.response?.data?.providerStatusCode || null,
      endpoint: error?.response?.data?.endpoint || null,
      originalMessage: error?.response?.data?.message || error?.message || null,
    });
    setErrorMessage(feedback.message);

    return feedback;
  }

  async function requestRoutingToken() {
    if (!ROUTING_CLIENT_EMAIL || !ROUTING_CLIENT_PASSWORD) {
      throw new Error(
        "As credenciais da API de roteamento nao foram configuradas no .env."
      );
    }

    const response = await routingApi.post(ROUTING_AUTH_PATH, {
      email: ROUTING_CLIENT_EMAIL,
      password: ROUTING_CLIENT_PASSWORD,
    });

    const token =
      response?.data?.access_token ||
      response?.data?.accessToken ||
      response?.data?.token;

    if (!token) {
      throw new Error(
        "A API de roteamento nao retornou um token em um formato suportado."
      );
    }

    localStorage.setItem(ROUTING_TOKEN_KEY, token);

    return token;
  }

  async function getRoutingToken(forceRefresh = false) {
    const storedToken = localStorage.getItem(ROUTING_TOKEN_KEY);

    if (storedToken && !forceRefresh) {
      return storedToken;
    }

    return requestRoutingToken();
  }

  async function getRequestHeaders(forceRefresh = false) {
    const token = await getRoutingToken(forceRefresh);

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async function sendRoutingRequest(config) {
    const activeCooldown = getActiveRoutingCooldown();

    if (activeCooldown) {
      throw createRoutingCooldownError(activeCooldown);
    }

    const requestKey = buildRoutingRequestKey(config);
    const pendingRequest = inFlightRoutingRequestsRef.current.get(requestKey);

    if (pendingRequest) {
      return pendingRequest;
    }

    const requestPromise = (async () => {
      try {
        const headers = await getRequestHeaders();

        return await routingApi({
          ...config,
          headers: {
            ...(config.headers || {}),
            ...headers,
          },
        });
      } catch (error) {
        const isProviderCredentialError =
          error?.response?.data?.code === ROUTING_PROVIDER_UNAUTHORIZED_CODE;

        if (error?.response?.status !== 401 || isProviderCredentialError) {
          throw error;
        }

        localStorage.removeItem(ROUTING_TOKEN_KEY);

        const refreshedHeaders = await getRequestHeaders(true);

        return routingApi({
          ...config,
          headers: {
            ...(config.headers || {}),
            ...refreshedHeaders,
          },
        });
      }
    })();

    inFlightRoutingRequestsRef.current.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      const feedback = getRoutingErrorFeedback(error);

      if (feedback.cooldown) {
        activateRoutingCooldown(feedback.cooldown);
      }

      throw error;
    } finally {
      if (inFlightRoutingRequestsRef.current.get(requestKey) === requestPromise) {
        inFlightRoutingRequestsRef.current.delete(requestKey);
      }
    }
  }

  function buildJsonRoutePayload(
    status = selectedRouteStatus,
    nextOperationalRouteId = operationalRouteId,
    nextOperationalRouteSource = operationalRouteSource
  ) {
    return {
      origin,
      destination,
      waypoints: waypoints.map((item) => item.address),
      routePoints,
      routeSegments,
      routePolyline,
      totalDistance,
      totalDuration,
      profile,
      situacao: status,
      IDrotaOperacional: nextOperationalRouteId || null,
      origemIDrotaOperacional: nextOperationalRouteSource || null,
    };
  }

  function applyLoadedRoute(routeData) {
    const parsedRoute = parseJsonRoute(routeData?.jsonrota);
    const parsedOptimizedRoute = parseJsonRoute(routeData?.jsonrotaotimizada);

    if (!parsedRoute) {
      setErrorMessage("A rota selecionada possui jsonrota invalido.");
      return;
    }

    const loadedOrigin = parsedRoute.origin || "";
    const loadedDestination = parsedRoute.destination || "";
    const loadedWaypoints = Array.isArray(parsedRoute.waypoints)
      ? parsedRoute.waypoints
      : [];
    const loadedPoints = Array.isArray(parsedRoute.routePoints)
      ? parsedRoute.routePoints
      : [];
    const loadedSegments = Array.isArray(parsedRoute.routeSegments)
      ? parsedRoute.routeSegments
      : [];
    const loadedPolyline = Array.isArray(parsedRoute.routePolyline)
      ? parsedRoute.routePolyline
      : [];

    setOrigin(loadedOrigin);
    setDestination(loadedDestination);
    setWaypoints(loadedWaypoints.map((address) => createWaypoint(address)));
    setRoutePoints(loadedPoints);
    setRouteSegments(loadedSegments);
    setRoutePolyline(loadedPolyline);
    setRouteOptimizationData(parsedOptimizedRoute);
    setTotalDistance(parsedRoute.totalDistance || 0);
    setTotalDuration(parsedRoute.totalDuration || 0);
    setRouteName(routeData?.nomerota || "");
    setSelectedRouteId(routeData?.IDrota || null);
    setLastFinalizedLotId(null);
    setLastFinalizedOperationalRouteId(null);
    setOperationalRouteId(
      Number(parsedRoute?.IDrotaOperacional) > 0
        ? Number(parsedRoute.IDrotaOperacional)
        : null
    );
    setOperationalRouteSource(parsedRoute?.origemIDrotaOperacional || null);
    setSelectedRouteStatus(Number(routeData?.situacao) || 0);
    setMicroPanelOpen(false);
    setSelectedMicroStopOrder(null);
    setSelectedMicroStop(null);
    setMicroMapPoints([]);
    setMicroPlanningDirty(false);
    setOrderSelectionPayload(null);
    setOrderSelectionError("");
    setOrderSelectionDirty(false);
    setErrorMessage("");
    setSuccessMessage(
      parsedRoute?.IDrotaOperacional
        ? `Rota salva carregada com o ID operacional ${parsedRoute.IDrotaOperacional}.`
        : "Rota carregada com sucesso."
    );
  }

  async function openAvailableOrderRoutesModal() {
    setShowAvailableOrderRoutesModal(true);
    setAvailableOrderRoutes([]);
    setAvailableOrderRoutesError("");
    setLoadingAvailableOrderRoutes(true);

    try {
      const response = await projectApi.get(
        "/roteamento/pedidos/rotas-disponiveis"
      );
      setAvailableOrderRoutes(
        Array.isArray(response?.data) ? response.data : []
      );
    } catch (error) {
      setAvailableOrderRoutesError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar as rotas disponiveis dos pedidos."
      );
    } finally {
      setLoadingAvailableOrderRoutes(false);
    }
  }

  async function handleSelectAvailableOrderRoute(routeItem) {
    const nextOperationalRouteId = Number(routeItem?.IDrota);
    const routeCities = Array.isArray(routeItem?.cidades)
      ? routeItem.cidades
      : [];

    if (!Number.isInteger(nextOperationalRouteId) || nextOperationalRouteId <= 0) {
      return;
    }

    setAvailableOrderRoutesError("");

    if (loadingCityCatalog) {
      setAvailableOrderRoutesError(
        "Aguarde o carregamento das cidades do IBGE."
      );
      return;
    }

    if (cityCatalogError) {
      setAvailableOrderRoutesError(cityCatalogError);
      return;
    }

    if (
      waypoints.length > 0 ||
      selectedRouteId ||
      microPlanningDirty ||
      orderSelectionDirty
    ) {
      const confirmation = await Swal.fire({
        icon: "question",
        title: `Carregar rota ${nextOperationalRouteId}?`,
        text: "A rota em edicao sera fechada e uma nova rota sera iniciada com as cidades selecionadas.",
        showCancelButton: true,
        confirmButtonText: "Carregar rota",
        cancelButtonText: "Cancelar",
        customClass: {
          container: "route-confirmation-alert",
        },
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    const resolvedCities = [];
    const unresolvedCities = [];
    const usedCities = new Set(
      [origin, destination]
        .map((city) => normalizeCityValue(city))
        .filter(Boolean)
    );

    routeCities.forEach((city) => {
      const rawCity = [city?.cidade, city?.uf].filter(Boolean).join(" - ");
      const resolvedCity = resolveCityOrNull(rawCity);

      if (!resolvedCity) {
        unresolvedCities.push(rawCity || "Cidade nao informada");
        return;
      }

      const normalizedCity = normalizeCityValue(resolvedCity);

      if (!usedCities.has(normalizedCity)) {
        usedCities.add(normalizedCity);
        resolvedCities.push(createWaypoint(resolvedCity));
      }
    });

    if (unresolvedCities.length > 0) {
      setAvailableOrderRoutesError(
        `As seguintes cidades nao foram reconhecidas no cadastro do IBGE: ${unresolvedCities.join(
          ", "
        )}.`
      );
      return;
    }

    if (resolvedCities.length === 0) {
      setAvailableOrderRoutesError(
        `A rota ${nextOperationalRouteId} nao possui cidades disponiveis.`
      );
      return;
    }

    setLoadingAvailableOrderRoutes(true);
    let selectionPayload;

    try {
      const selectionResponse = await projectApi.get(
        `/roteamento/pedidos/rotas/${nextOperationalRouteId}/selecao`
      );
      selectionPayload = selectionResponse?.data || null;
    } catch (error) {
      setAvailableOrderRoutesError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar os pedidos da rota selecionada."
      );
      return;
    } finally {
      setLoadingAvailableOrderRoutes(false);
    }

    if (!selectionPayload?.cidades?.length) {
      setAvailableOrderRoutesError(
        `A rota ${nextOperationalRouteId} nao possui pedidos disponiveis para selecao.`
      );
      return;
    }

    setWaypoints(resolvedCities);
    setRoutePoints([]);
    setRouteSegments([]);
    setRoutePolyline([]);
    setRouteOptimizationData(null);
    setTotalDistance(0);
    setTotalDuration(0);
    setOperationalRouteId(nextOperationalRouteId);
    setOperationalRouteSource("pedidos");
    setSelectedRouteId(null);
    setSelectedRouteStatus(0);
    setLastFinalizedLotId(null);
    setLastFinalizedOperationalRouteId(null);
    setRouteName(`Rota ${nextOperationalRouteId}`);
    setMacroStopsPayload(null);
    setMacroStopsError("");
    setOrderSelectionPayload(selectionPayload);
    setOrderSelectionError("");
    setOrderSelectionDirty(true);
    setMicroPanelOpen(false);
    setSelectedMicroStopOrder(null);
    setSelectedMicroStop(null);
    setMicroMapPoints([]);
    setMicroPlanningDirty(false);
    setErrorMessage("");
    setSuccessMessage(
      `Rota ${nextOperationalRouteId} carregada: ${resolvedCities.length} cidade(s) e ${
        Number(routeItem?.totalPedidos) || 0
      } pedido(s) disponivel(is).`
    );
    setShowAvailableOrderRoutesModal(false);
  }

  async function fetchRoutesByStatus(status) {
    const response = await projectApi.get(`/roteamento/rotas?situacao=${status}`);
    const list = Array.isArray(response?.data) ? response.data : [];

    if (status === 1) {
      return [...list]
        .sort((a, b) => getRouteDateValue(b) - getRouteDateValue(a))
        .slice(0, 30);
    }

    return list;
  }

  async function openRoutesModal() {
    setShowRouteModal(true);
    setModalStatusFilter(0);
    setModalError("");
    setAvailableRoutes([]);
    setModalLoading(true);

    try {
      const list = await fetchRoutesByStatus(0);
      setAvailableRoutes(list);
    } catch (error) {
      setModalError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar as rotas."
      );
    } finally {
      setModalLoading(false);
    }
  }

  async function changeModalFilter(status) {
    if (modalStatusFilter === status) {
      return;
    }

    setModalStatusFilter(status);
    setModalError("");
    setAvailableRoutes([]);
    setModalLoading(true);

    try {
      const list = await fetchRoutesByStatus(status);
      setAvailableRoutes(list);
    } catch (error) {
      setModalError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar as rotas."
      );
    } finally {
      setModalLoading(false);
    }
  }

  async function handleSelectRoute(routeItem) {
    if (!routeItem?.IDrota) {
      return;
    }

    if (
      (microPlanningDirty || orderSelectionDirty) &&
      !window.confirm(
        "Existem alteracoes de pedidos nao salvas. Deseja abrir outra rota e descarta-las?"
      )
    ) {
      return;
    }

    setOpeningRoute(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await projectApi.get(`/roteamento/rota/${routeItem.IDrota}`);
      applyLoadedRoute(response.data);
      setShowRouteModal(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar os dados da rota selecionada."
      );
    } finally {
      setOpeningRoute(false);
    }
  }

  const mapOrderSelectionToStops = useCallback((stopNames) => {
    const selectionCities = Array.isArray(orderSelectionPayload?.cidades)
      ? orderSelectionPayload.cidades
      : [];
    const citiesByName = new Map();

    selectionCities.forEach((city, selectionIndex) => {
      const key = normalizeCityIdentity(city?.nome);
      const current = citiesByName.get(key) || [];
      current.push({ ...city, selectionIndex });
      citiesByName.set(key, current);
    });

    return stopNames.map((name, index) => {
      const key = normalizeCityIdentity(name);
      const matches = citiesByName.get(key) || [];
      const matchedCity = matches.shift() || null;
      citiesByName.set(key, matches);

      return {
        ...(matchedCity || {
          IDsPedidoSelecionados: [],
          pedidosAgrupados: [],
          totalPedidos: 0,
          salva: true,
          selectionIndex: null,
        }),
        ordem: index + 1,
        nome: name,
      };
    });
  }, [orderSelectionPayload]);

  function buildOrderSelectionRequest(stopNames) {
    if (!orderSelectionPayload || loadingOrderSelection) {
      throw new Error("Aguarde o carregamento dos pedidos da rota.");
    }

    const mappedCities = mapOrderSelectionToStops(stopNames);
    const emptyCities = mappedCities.filter(
      (city) =>
        Number(city.totalPedidos) > 0 &&
        (!Array.isArray(city.IDsPedidoSelecionados) ||
          city.IDsPedidoSelecionados.length === 0)
    );

    if (emptyCities.length > 0) {
      throw new Error(
        `Mantenha ao menos um pedido selecionado em: ${emptyCities
          .map((city) => city.nome)
          .join(", ")}. Para retirar todas as entregas, remova a cidade da rota.`
      );
    }

    return {
      cidades: mappedCities.map((city) => {
        const selectedIds = new Set(
          (city.IDsPedidoSelecionados || []).map((IDpedido) => Number(IDpedido))
        );
        let plannedOrder = 0;
        const paradas = (city.pedidosAgrupados || [])
          .map((group) => {
            const IDsPedido = (group.pedidos || [])
              .map((pedido) => Number(pedido.IDpedido))
              .filter((IDpedido) => selectedIds.has(IDpedido));

            if (IDsPedido.length === 0) {
              return null;
            }

            plannedOrder += 1;
            return {
              ordemPlanejada: plannedOrder,
              IDcliente: Number(group.IDcliente),
              IDendereco: Number(group.IDendereco),
              IDsPedido,
            };
          })
          .filter(Boolean);

        return {
          ordem: city.ordem,
          paradas,
        };
      }),
    };
  }

  async function saveOrUpdateRoute(
    nextStatus,
    nextOperationalRouteId = operationalRouteId,
    nextOperationalRouteSource = operationalRouteSource
  ) {
    const normalizedName = routeName.trim();

    if (!normalizedName) {
      setErrorMessage("Informe o nome da rota antes de salvar.");
      return false;
    }

    if (!origin.trim() || !destination.trim()) {
      setErrorMessage("Informe origem e destino antes de salvar.");
      return false;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return false;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return false;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin || !resolvedDestination) {
      const message = CITY_NOT_ALLOWED_MESSAGE;
      setErrorMessage(message);
      showValidationAlert(message);
      return false;
    }

    if (resolvedOrigin !== origin) {
      setOrigin(resolvedOrigin);
    }

    if (resolvedDestination !== destination) {
      setDestination(resolvedDestination);
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < waypoints.length; index += 1) {
      const item = waypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        const message = `Parada ${index + 1}: ${CITY_NOT_ALLOWED_MESSAGE}`;
        setErrorMessage(message);
        showValidationAlert(message);
        return false;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    if (
      resolvedWaypoints.some(
        (item, index) => item.address !== waypoints[index]?.address
      )
    ) {
      setWaypoints(resolvedWaypoints);
    }

    const jsonRoutePayload = {
      ...buildJsonRoutePayload(
        nextStatus,
        nextOperationalRouteId,
        nextOperationalRouteSource
      ),
      origin: resolvedOrigin,
      destination: resolvedDestination,
      waypoints: resolvedWaypoints.map((item) => item.address),
    };

    const payload = {
      nomerota: normalizedName,
      jsonrota: jsonRoutePayload,
      jsonrotaotimizada: routeOptimizationData,
      situacao: nextStatus,
    };
    let selectionRequest;

    try {
      selectionRequest = buildOrderSelectionRequest([
        ...resolvedWaypoints.map((item) => item.address),
        resolvedDestination,
      ]);
    } catch (error) {
      setErrorMessage(error?.message || "Revise a selecao de pedidos da rota.");
      return false;
    }

    setSavingRoute(true);
    setErrorMessage("");
    setSuccessMessage("");
    let routePersisted = false;

    try {
      let effectiveRouteId = selectedRouteId;

      if (selectedRouteId) {
        console.log("[saveOrUpdateRoute] PUT", {
          url: `/roteamento/rota/${selectedRouteId}`,
          payload,
        });
        await projectApi.put(`/roteamento/rota/${selectedRouteId}`, payload);
        routePersisted = true;
      } else {
        const response = await projectApi.post("/roteamento/rota", payload);
        effectiveRouteId = response?.data?.IDrota || null;
        routePersisted = Boolean(effectiveRouteId);

        if (!effectiveRouteId) {
          throw new Error("A API nao retornou o identificador da rota salva.");
        }

        setSelectedRouteId(effectiveRouteId);
      }

      const selectionResponse = await projectApi.put(
        `/roteamento/rotas/${effectiveRouteId}/selecao-pedidos`,
        selectionRequest
      );
      setOrderSelectionPayload(selectionResponse?.data || null);
      setOrderSelectionDirty(false);
      setOrderSelectionError("");

      try {
        const stopsResponse = await projectApi.get(
          `/roteamento/rotas/${effectiveRouteId}/paradas`
        );
        setMacroStopsPayload(stopsResponse?.data || null);
      } catch (stopsError) {
        console.log(stopsError);
      }

      setSelectedRouteStatus(nextStatus);
      setSuccessMessage(
        `Rota${nextOperationalRouteId ? ` ${nextOperationalRouteId}` : ""} ${
          selectedRouteId ? "alterada" : "salva"
        } com a selecao de pedidos salva.`
      );

      return true;
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          (routePersisted
            ? "A rota foi salva, mas nao foi possivel salvar a selecao de pedidos. Clique em Alterar para tentar novamente."
            : "Nao foi possivel salvar os dados da rota.")
      );
      return false;
    } finally {
      setSavingRoute(false);
    }
  }

  async function handleSaveRoute() {
    if (isRouteReadOnly) {
      setErrorMessage("Rotas finalizadas nao podem ser alteradas.");
      return;
    }

    await saveOrUpdateRoute(0);
  }

  async function requestOperationalRouteId() {
    const routeResult = await Swal.fire({
      title: "Informar rota",
      text: "Informe o ID da rota que sera salvo e enviado ao sistema interno.",
      input: "number",
      inputLabel: "ID da rota",
      inputPlaceholder: "Ex.: 12345",
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      inputAttributes: {
        min: "1",
        step: "1",
      },
      inputValidator: (value) => {
        const parsedValue = Number(value);

        if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
          return "Informe um ID de rota valido.";
        }

        return undefined;
      },
    });

    return routeResult.isConfirmed ? Number(routeResult.value) : null;
  }

  function buildExportRows() {
    if (routePoints.length > 0) {
      return routePoints.map((point, index) => ({
        ...splitCityAndUf(point.address),
        distancia: formatDistance(point.distance_from_previous ?? 0),
        duracao: formatDuration(point.duration_from_previous ?? 0),
      }));
    }

    const addresses = [origin, ...waypoints.map((item) => item.address), destination].filter(
      Boolean
    );

    return addresses.map((address, index) => ({
      ...splitCityAndUf(address),
      distancia: index === 0 ? "0 km" : "-",
      duracao: index === 0 ? "0 min" : "-",
    }));
  }

  function exportRouteAsExcel() {
    const rows = buildExportRows();

    if (rows.length === 0) {
      setErrorMessage("Nao ha dados de rota para exportar.");
      return;
    }

    const csvLines = [
      ["Nome da rota", routeName || "Rota sem nome"].join(";"),
      ["ID da rota", operationalRouteId || "-"].join(";"),
      ["Origem", origin || "-"].join(";"),
      ["Destino", destination || "-"].join(";"),
      "",
      ["Cidade", "UF", "KM ponto anterior", "Tempo ponto anterior"].join(";"),
      ...rows.map((row) =>
        [
          `"${String(row.cidade || "-").replace(/"/g, '""')}"`,
          row.uf,
          row.distancia,
          row.duracao,
        ].join(";")
      ),
      "",
      ["Total em KM", formatDistance(totalDistance)].join(";"),
      ["Tempo total", formatDuration(totalDuration)].join(";"),
    ];

    const blob = new Blob(["\uFEFF" + csvLines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });

    downloadFile(blob, `${sanitizeFileName(routeName || "rota")}.csv`);
    setSuccessMessage("Arquivo Excel gerado com sucesso.");
  }

  async function exportRouteAsPdf() {
    if (!selectedRouteId) {
      setErrorMessage("Abra uma rota finalizada antes de gerar o PDF.");
      return;
    }

    let pdfLotId = lastFinalizedLotId;
    const pdfOperationalRouteId =
      lastFinalizedOperationalRouteId || operationalRouteId || selectedRouteId;

    if (!pdfLotId) {
      const loteResult = await Swal.fire({
        title: "Gerar PDF",
        text: "Informe o ID do lote finalizado que deseja imprimir.",
        input: "number",
        inputLabel: "ID do lote",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        inputAttributes: {
          min: "1",
          step: "1",
        },
        inputValidator: (value) => {
          const parsedValue = Number(value);

          if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
            return "Informe um ID de lote valido.";
          }

          return undefined;
        },
      });

      if (!loteResult.isConfirmed) {
        return;
      }

      pdfLotId = Number(loteResult.value);
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      setErrorMessage("Nao foi possivel abrir a janela para gerar o PDF.");
      return;
    }

    let finalization = null;

    try {
      const query = `?IDlote=${encodeURIComponent(
        pdfLotId
      )}&IDrota=${encodeURIComponent(pdfOperationalRouteId)}`;
      const response = await projectApi.get(
        `/roteamento/rotas/${selectedRouteId}/finalizacao${query}`
      );
      finalization = response?.data || null;
    } catch (error) {
      printWindow.close();
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar os pedidos finalizados para o PDF."
      );
      return;
    }

    const rows = Array.isArray(finalization?.pedidos)
      ? finalization.pedidos
      : [];

    if (rows.length === 0) {
      printWindow.close();
      setErrorMessage("Nao ha pedidos finalizados para gerar o PDF.");
      return;
    }

    const rowMarkup = rows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.IDlote)}</td>
            <td>${escapeHtml(row.ordem)}</td>
            <td>${escapeHtml(row.IDpedido)}</td>
            <td>${escapeHtml(row.cidade)}</td>
            <td>${escapeHtml(row.uf)}</td>
          </tr>
        `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(routeName || "Rota")}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #183524; }
            h1 { margin: 0 0 6px; font-size: 24px; }
            p { margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #c8d7cc; padding: 8px; text-align: left; font-size: 12px; }
            th { background: #edf5ee; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(routeName || "Rota sem nome")}</h1>
          <p><strong>ID da rota:</strong> ${escapeHtml(finalization.IDrota)}</p>
          <p><strong>ID do lote:</strong> ${escapeHtml(finalization.IDlote)}</p>
          <p><strong>Total de pedidos:</strong> ${escapeHtml(finalization.totalPedidos)}</p>
          <p><strong>Origem:</strong> ${escapeHtml(origin || "-")}</p>
          <p><strong>Destino:</strong> ${escapeHtml(destination || "-")}</p>
          <table>
            <thead>
              <tr>
                <th>ID Lote</th>
                <th>Ordem de carregamento</th>
                <th>ID Pedido</th>
                <th>Cidade</th>
                <th>UF</th>
              </tr>
            </thead>
            <tbody>${rowMarkup}</tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    setSuccessMessage("Visualizacao para PDF aberta com sucesso.");
  }

  async function handleExportRoute() {
    const result = await Swal.fire({
      title: "Gerar arquivo",
      text: "Escolha o formato de exportacao da rota.",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "PDF",
      denyButtonText: "Excel",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      await exportRouteAsPdf();
    }

    if (result.isDenied) {
      exportRouteAsExcel();
    }
  }

  async function handleExportRouteJson() {
    if (!selectedRouteId) {
      setErrorMessage("Abra ou salve uma rota antes de exportar e finalizar.");
      return;
    }

    if (isRouteReadOnly) {
      setErrorMessage("Esta rota ja foi finalizada e nao pode ser processada novamente.");
      return;
    }

    if (
      loadingOrderSelection ||
      orderSelectionDirty ||
      orderSelectionPayload?.salva !== true
    ) {
      setErrorMessage(
        "Salve ou altere a rota para gravar a selecao dos pedidos antes de finalizar."
      );
      return;
    }

    const incompleteStops = macroStops.filter((stop) => !stop.concluida);
    if (macroStops.length === 0 || incompleteStops.length > 0) {
      setErrorMessage(
        incompleteStops.length > 0
          ? `Finalize o planejamento das cidades: ${incompleteStops
              .map((stop) => stop.nome)
              .join(", ")}.`
          : "A rota nao possui cidades planejadas para finalizar."
      );
      return;
    }

    let nextOperationalRouteId = operationalRouteId;
    let nextOperationalRouteSource = operationalRouteSource;

    if (!nextOperationalRouteId) {
      nextOperationalRouteId = await requestOperationalRouteId();

      if (!nextOperationalRouteId) {
        return;
      }

      nextOperationalRouteSource = "manual";
      setOperationalRouteId(nextOperationalRouteId);
      setOperationalRouteSource(nextOperationalRouteSource);
    }

    let IDlote = nextOperationalRouteId;

    if (nextOperationalRouteSource === "pedidos") {
      const confirmationResult = await Swal.fire({
        icon: "question",
        title: "Exportar rota - finalizar",
        text: `A rota ${nextOperationalRouteId} sera finalizada com o mesmo ID de lote: ${nextOperationalRouteId}.`,
        showCancelButton: true,
        confirmButtonText: "Exportar e finalizar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmationResult.isConfirmed) {
        return;
      }
    } else {
      const loteResult = await Swal.fire({
        title: "Exportar rota - finalizar",
        text: `Rota ${nextOperationalRouteId}: informe o ID do lote que recebera a sequencia de pedidos.`,
        input: "number",
        inputLabel: "ID do lote",
        inputPlaceholder: "Ex.: 12345",
        showCancelButton: true,
        confirmButtonText: "Exportar e finalizar",
        cancelButtonText: "Cancelar",
        inputAttributes: {
          min: "1",
          step: "1",
        },
        inputValidator: (value) => {
          const parsedValue = Number(value);

          if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
            return "Informe um ID de lote valido.";
          }

          return undefined;
        },
      });

      if (!loteResult.isConfirmed) {
        return;
      }

      IDlote = Number(loteResult.value);
    }

    setExportingRouteJson(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const routeSaved = await saveOrUpdateRoute(
        0,
        nextOperationalRouteId,
        nextOperationalRouteSource
      );

      if (!routeSaved) {
        return;
      }

      const response = await projectApi.post(
        `/roteamento/rotas/${selectedRouteId}/exportacao/finalizar`,
        { IDrota: nextOperationalRouteId, IDlote }
      );
      const totalPedidos = Number(response?.data?.totalPedidos) || 0;
      setLastFinalizedLotId(IDlote);
      setLastFinalizedOperationalRouteId(nextOperationalRouteId);
      setSelectedRouteStatus(1);
      setSuccessMessage(
        `Rota ${nextOperationalRouteId} finalizada no lote ${IDlote}. ${totalPedidos} pedido(s) salvo(s) com sucesso.`
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel exportar e finalizar a rota."
      );
    } finally {
      setExportingRouteJson(false);
    }
  }

  function buildOptimizeQuery(
    nextWaypoints = waypoints,
    shouldOptimize = true,
    includeGeometry = true,
    nextOrigin = origin,
    nextDestination = destination
  ) {
    const params = new URLSearchParams();

    params.append("origin", nextOrigin);
    params.append("destination", nextDestination);
    params.append("optimize", shouldOptimize ? "true" : "false");
    params.append("profile", profile);
    params.append("include_geometry", includeGeometry ? "true" : "false");

    nextWaypoints.forEach((item) => {
      params.append("waypoints[]", item.address);
    });

    return params.toString();
  }

  function getOptimizeEndpoint() {
    return "/routes/optimizeplus";
  }

  function formatDistance(distanceInMeters) {
    if (!distanceInMeters) {
      return "0 km";
    }

    return `${(distanceInMeters / 1000).toFixed(1)} km`;
  }

  function formatDuration(durationInSeconds) {
    if (!durationInSeconds) {
      return "0 min";
    }

    const totalMinutes = Math.round(durationInSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
      return `${minutes} min`;
    }

    return `${hours}h ${minutes}min`;
  }

  function isPlaceholderAddress(address) {
    if (!address) {
      return true;
    }

    return /^waypoint\s+\d+$/i.test(address.trim());
  }

  function resolveWaypointAddress(
    address,
    waypointAddresses,
    originAddress,
    destinationAddress
  ) {
    if (!address) {
      return address;
    }

    const trimmedAddress = address.trim();

    if (/^origin$/i.test(trimmedAddress)) {
      return originAddress;
    }

    if (/^destination$/i.test(trimmedAddress)) {
      return destinationAddress;
    }

    if (!isPlaceholderAddress(trimmedAddress)) {
      return address;
    }

    const matchedNumber = trimmedAddress.match(/\d+/);
    const waypointIndex = matchedNumber ? Number(matchedNumber[0]) - 1 : -1;

    if (
      waypointIndex < 0 ||
      waypointIndex >= waypointAddresses.length ||
      !waypointAddresses[waypointIndex]
    ) {
      return address;
    }

    return waypointAddresses[waypointIndex];
  }

  function buildNormalizedOptimizedRoute(optimizedRoute, submittedAddresses) {
    const totalPoints = submittedAddresses.length;

    if (totalPoints === 0) {
      return [];
    }

    if (totalPoints === 1) {
      return [
        {
          address: submittedAddresses[0],
          order: 0,
          coordinates: null,
          distance_from_previous: 0,
          duration_from_previous: 0,
        },
      ];
    }

    const originAddress = submittedAddresses[0];
    const destinationAddress = submittedAddresses[totalPoints - 1];
    const middleAddresses = submittedAddresses.slice(1, -1);
    const apiRoute = Array.isArray(optimizedRoute) ? optimizedRoute : [];
    const apiOrigin = apiRoute[0] || null;
    const apiDestination = apiRoute.length > 1 ? apiRoute[apiRoute.length - 1] : null;
    const apiMiddle = apiRoute.length > 2 ? apiRoute.slice(1, -1) : [];
    const orderedMiddle = [];
    const usedWaypointIndexes = new Set();

    function getWaypointIndex(item) {
      const rawJobId = item?.job_id;
      const jobId =
        rawJobId === null || rawJobId === undefined || rawJobId === ""
          ? Number.NaN
          : Number(rawJobId);

      if (
        Number.isInteger(jobId) &&
        jobId >= 0 &&
        jobId < middleAddresses.length &&
        !usedWaypointIndexes.has(jobId)
      ) {
        return jobId;
      }

      const resolvedAddress = resolveWaypointAddress(
        item?.address,
        middleAddresses,
        originAddress,
        destinationAddress
      );
      const normalizedAddress = normalizeCityValue(resolvedAddress);

      if (!normalizedAddress) {
        return -1;
      }

      return middleAddresses.findIndex(
        (address, index) =>
          !usedWaypointIndexes.has(index) &&
          normalizeCityValue(address) === normalizedAddress
      );
    }

    function buildPoint(address, source, index) {
      const latitude = Number(source?.coordinates?.lat);
      const longitude = Number(source?.coordinates?.lng);
      const rawDistance = source?.distance_from_previous;
      const rawDuration = source?.duration_from_previous;
      const parsedDistance = Number(rawDistance);
      const parsedDuration = Number(rawDuration);

      return {
        address,
        order: index,
        coordinates:
          Number.isFinite(latitude) && Number.isFinite(longitude)
            ? { lat: latitude, lng: longitude }
            : null,
        distance_from_previous:
          rawDistance !== null &&
          rawDistance !== undefined &&
          Number.isFinite(parsedDistance)
            ? parsedDistance
            : index === 0
            ? 0
            : null,
        duration_from_previous:
          rawDuration !== null &&
          rawDuration !== undefined &&
          Number.isFinite(parsedDuration)
            ? parsedDuration
            : index === 0
            ? 0
            : null,
      };
    }

    apiMiddle.forEach((item) => {
      const waypointIndex = getWaypointIndex(item);

      if (waypointIndex >= 0) {
        usedWaypointIndexes.add(waypointIndex);
        orderedMiddle.push({
          address: middleAddresses[waypointIndex],
          source: item,
        });
      }
    });

    middleAddresses.forEach((address, index) => {
      if (!usedWaypointIndexes.has(index)) {
        orderedMiddle.push({ address, source: null });
      }
    });

    const orderedEntries = [
      { address: originAddress, source: apiOrigin },
      ...orderedMiddle,
      { address: destinationAddress, source: apiDestination },
    ];

    return orderedEntries.map((entry, index) =>
      buildPoint(entry.address, entry.source, index)
    );
  }

  function extractRoutePolyline(data) {
    const rawGeometry =
      data?.geometry || data?.route_geometry || data?.geojson || null;
    const { coordinates, format } = extractGeometryCoordinates(rawGeometry);

    if (!Array.isArray(coordinates)) {
      return [];
    }

    const normalizedCoordinates = coordinates
      .filter(
        (coordinate) =>
          Array.isArray(coordinate) &&
          coordinate.length >= 2 &&
          Number.isFinite(coordinate[0]) &&
          Number.isFinite(coordinate[1])
      );

    if (format === "latlng") {
      return normalizedCoordinates.map((coordinate) => [coordinate[0], coordinate[1]]);
    }

    return normalizedCoordinates.map((coordinate) => [coordinate[1], coordinate[0]]);
  }

  async function fetchRoutePreview(
    nextWaypoints = waypoints,
    shouldOptimize = true
  ) {
    const response = await sendRoutingRequest({
      method: "get",
      url: `${getOptimizeEndpoint()}?${buildOptimizeQuery(
        nextWaypoints,
        shouldOptimize,
        true
      )}`,
    });

    return response.data;
  }

  function markOrderSelectionUnsaved() {
    if (!orderSelectionPayload) {
      return;
    }

    setOrderSelectionPayload((current) =>
      current
        ? {
            ...current,
            salva: false,
            cidades: (current.cidades || []).map((city) => ({
              ...city,
              salva: false,
            })),
          }
        : current
    );
    setOrderSelectionDirty(true);
  }

  function addWaypoint() {
    const trimmedValue = cityInput.trim();

    if (!trimmedValue) {
      return;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return;
    }

    const resolvedCity = resolveCityOrNull(trimmedValue);

    if (!resolvedCity) {
      const message = CITY_NOT_ALLOWED_MESSAGE;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    const cityAlreadyIncluded = [
      origin,
      destination,
      ...waypoints.map((item) => item.address),
    ].some(
      (city) => normalizeCityValue(city) === normalizeCityValue(resolvedCity)
    );

    if (cityAlreadyIncluded) {
      setErrorMessage(`${resolvedCity} ja esta incluida na rota.`);
      return;
    }

    const updatedWaypoints = [
      ...waypoints,
      createWaypoint(resolvedCity),
    ];

    setWaypoints(updatedWaypoints);
    markOrderSelectionUnsaved();

    if (routePoints.length > 0) {
      rebuildRoute(updatedWaypoints);
    }

    setCityInput("");
    setErrorMessage("");
  }

  function removeWaypoint(id) {
    const updatedWaypoints = waypoints.filter((item) => item.id !== id);
    const hadCalculatedRoute =
      routePoints.length > 0 ||
      routeSegments.length > 0 ||
      routePolyline.length > 0 ||
      routeOptimizationData !== null;

    setWaypoints(updatedWaypoints);
    markOrderSelectionUnsaved();
    setRoutePoints([]);
    setRouteSegments([]);
    setRoutePolyline([]);
    setRouteOptimizationData(null);
    setTotalDistance(0);
    setTotalDuration(0);
    setMapFocusTarget({
      id: `route-cleared-${Date.now()}`,
      points: [DEFAULT_CENTER],
      zoom: 5,
    });
    setErrorMessage("");
    setSuccessMessage(
      hadCalculatedRoute
        ? "Cidade removida. Clique em Rotear para calcular a rota novamente."
        : ""
    );
  }

  async function optimizeRoute() {
    const activeCooldown = getActiveRoutingCooldown();

    if (activeCooldown) {
      setErrorMessage(activeCooldown.message);
      return;
    }

    if (!origin.trim() || !destination.trim()) {
      setErrorMessage("Informe a cidade de origem e a cidade de destino.");
      return;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin) {
      const message = `Origem: ${CITY_NOT_ALLOWED_MESSAGE}`;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    if (!resolvedDestination) {
      const message = `Destino: ${CITY_NOT_ALLOWED_MESSAGE}`;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < waypoints.length; index += 1) {
      const item = waypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        const message = `Parada ${index + 1}: ${CITY_NOT_ALLOWED_MESSAGE}`;
        setErrorMessage(message);
        showValidationAlert(message);
        return;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    setOrigin(resolvedOrigin);
    setDestination(resolvedDestination);
    setWaypoints(resolvedWaypoints);

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const optimizeQuery = buildOptimizeQuery(
        resolvedWaypoints,
        true,
        true,
        resolvedOrigin,
        resolvedDestination
      );
      const optimizeUrl = `${getOptimizeEndpoint()}?${optimizeQuery}`;

      const response = await sendRoutingRequest({
        method: "get",
        url: optimizeUrl,
      });

      const data = response.data;
      const submittedAddresses = [
        resolvedOrigin,
        ...resolvedWaypoints.map((item) => item.address),
        resolvedDestination,
      ];
      const normalizedRoute = buildNormalizedOptimizedRoute(
        data.optimized_route || [],
        submittedAddresses
      );
      const polyline = extractRoutePolyline(data);
      const optimizedSegments = Array.isArray(data.segments)
        ? data.segments
        : [];
      const orderedStops = normalizedRoute
        .slice(1, -1)
        .map((item) => createWaypoint(item.address));

      setWaypoints(orderedStops);
      setRoutePoints(normalizedRoute);
      setRouteSegments(optimizedSegments);
      markOrderSelectionUnsaved();
      setRoutePolyline(polyline);
      setRouteOptimizationData({
        ...data,
        requestedOrigin: resolvedOrigin,
        requestedDestination: resolvedDestination,
        requestedWaypoints: resolvedWaypoints.map((item) => item.address),
      });
      setTotalDistance(data.total_distance || 0);
      setTotalDuration(data.total_duration || 0);
      const successText = operationalRouteId
        ? `Rota ${operationalRouteId} roteada com sucesso.`
        : "Rota calculada com sucesso.";
      const cacheText =
        data?.cache_info?.hit === true
          ? " Resultado recuperado do cache, sem novo consumo da API."
          : "";

      clearRoutingCooldown();
      setSuccessMessage(`${successText}${cacheText}`);
    } catch (error) {
      setRouteOptimizationData(null);
      handleRoutingError(error);
    } finally {
      setLoading(false);
    }
  }

  async function rebuildRoute(nextWaypoints) {
    const activeCooldown = getActiveRoutingCooldown();

    if (activeCooldown) {
      setErrorMessage(activeCooldown.message);
      return;
    }

    if (!origin.trim() || !destination.trim()) {
      return;
    }

    if (loadingCityCatalog || cityCatalogError) {
      return;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin || !resolvedDestination) {
      return;
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < nextWaypoints.length; index += 1) {
      const item = nextWaypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        return;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    const orderedAddresses = [
      resolvedOrigin,
      ...resolvedWaypoints.map((item) => item.address),
      resolvedDestination,
    ];

    if (orderedAddresses.length < 2) {
      setRoutePoints([]);
      setRouteSegments([]);
      setRoutePolyline([]);
      setTotalDistance(0);
      setTotalDuration(0);
      return;
    }

    setReordering(true);
    setErrorMessage("");
    setSuccessMessage("");
    setRouteOptimizationData(null);

    try {
      const previewData = await fetchRoutePreview(resolvedWaypoints, false);
      const normalizedRoute = buildNormalizedOptimizedRoute(
        previewData.optimized_route || [],
        orderedAddresses
      );
      const manualPolyline = extractRoutePolyline(previewData);
      const previewSegments = Array.isArray(previewData.segments)
        ? previewData.segments
        : [];

      setRouteSegments(previewSegments);
      setRoutePoints(normalizedRoute);
      markOrderSelectionUnsaved();
      setRoutePolyline(manualPolyline.length > 1 ? manualPolyline : []);
      setTotalDistance(previewData.total_distance || 0);
      setTotalDuration(previewData.total_duration || 0);
    } catch (error) {
      setRouteOptimizationData(null);
      handleRoutingError(error);
    } finally {
      setReordering(false);
    }
  }

  function reorderWaypointByIds(sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) {
      return;
    }

    const currentIndex = waypoints.findIndex((item) => item.id === sourceId);
    const targetIndex = waypoints.findIndex((item) => item.id === targetId);

    if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) {
      return;
    }

    const reordered = [...waypoints];
    const [movedItem] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);

    setWaypoints(reordered);
    markOrderSelectionUnsaved();
    setDraggedWaypointId(null);
    setDragOverWaypointId(null);
    rebuildRoute(reordered);
  }

  function moveWaypointToIndex(waypointId, nextPosition) {
    if (!waypointId || !Number.isInteger(nextPosition)) {
      return;
    }

    const currentIndex = waypoints.findIndex((item) => item.id === waypointId);

    if (currentIndex < 0) {
      return;
    }

    const targetIndex = Math.max(0, Math.min(waypoints.length - 1, nextPosition - 1));

    if (currentIndex === targetIndex) {
      setShowOrderModal(false);
      setSelectedWaypointForOrder(null);
      setOrderInput("");
      return;
    }

    const reordered = [...waypoints];
    const [movedItem] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);

    setWaypoints(reordered);
    markOrderSelectionUnsaved();
    setShowOrderModal(false);
    setSelectedWaypointForOrder(null);
    setOrderInput("");
    rebuildRoute(reordered);
  }

  function openOrderModal(item, index) {
    setSelectedWaypointForOrder(item);
    setOrderInput(String(index + 1));
    setShowOrderModal(true);
    setErrorMessage("");
    setSuccessMessage("");
  }

  function closeOrderModal() {
    setShowOrderModal(false);
    setSelectedWaypointForOrder(null);
    setOrderInput("");
  }

  function handleOrderSubmit(event) {
    event.preventDefault();

    if (!selectedWaypointForOrder) {
      return;
    }

    const parsedPosition = Number.parseInt(orderInput, 10);

    if (!Number.isInteger(parsedPosition) || parsedPosition < 1 || parsedPosition > waypoints.length) {
      setErrorMessage(`Informe uma posicao entre 1 e ${waypoints.length}.`);
      return;
    }

    moveWaypointToIndex(selectedWaypointForOrder.id, parsedPosition);
  }

  const waypointDistanceLabels = useMemo(
    () => routePoints.slice(1, -1).map((point) => formatDistance(point?.distance_from_previous)),
    [routePoints]
  );

  function normalizeFieldValue(setter, value) {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
      return;
    }

    const resolved = resolveCityOrNull(value);

    if (resolved) {
      setter(resolved);
      setErrorMessage("");
      return;
    }

    setErrorMessage(CITY_NOT_ALLOWED_MESSAGE);
    showValidationAlert(CITY_NOT_ALLOWED_MESSAGE);
    setter("");
  }

  const segmentLabels = routeSegments.map((segment, index) => {
    const from = segment.coordinates?.from;
    const to = segment.coordinates?.to;

    if (!isValidLatLng(from?.lat, from?.lng) || !isValidLatLng(to?.lat, to?.lng)) {
      return null;
    }

    return {
      id: `${segment.from}-${segment.to}-${index}`,
      position: [(from.lat + to.lat) / 2, (from.lng + to.lng) / 2],
      label: formatDistance(segment.distance),
    };
  });

  const pointConnectionLine = routePoints
    .filter((point) => hasValidCoordinates(point))
    .map((point) => [point.coordinates.lat, point.coordinates.lng]);

  const fallbackSegmentLabels =
    routeSegments.length > 0
      ? segmentLabels
      : routePoints.slice(1).map((point, index) => {
          const previousPoint = routePoints[index];
          const from = previousPoint?.coordinates;
          const to = point?.coordinates;

          if (!from || !to) {
            return null;
          }

          return {
            id: `${previousPoint.address}-${point.address}-${index}`,
            position: [(from.lat + to.lat) / 2, (from.lng + to.lng) / 2],
            label: formatDistance(point.distance_from_previous),
          };
        });

  function createOrderIcon(order, type) {
    const markerLabel =
      type === "origin" || type === "destination" ? "" : String(order);

    return L.divIcon({
      className: "",
      html: `<div class="route-point-marker ${type}">${markerLabel}</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
  }

  function createSegmentIcon(label) {
    return L.divIcon({
      className: "",
      html: `<div class="route-segment-label">${label}</div>`,
      iconSize: [72, 24],
      iconAnchor: [36, 12],
    });
  }

  function createMicroClientIcon() {
    return L.divIcon({
      className: "",
      html: '<div class="route-micro-client-marker"></div>',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
  }

  const macroStops = useMemo(() => {
    if (!selectedRouteId && !orderSelectionPayload) {
      return [];
    }

    const storedStops = Array.isArray(macroStopsPayload?.paradas)
      ? macroStopsPayload.paradas
      : [];
    const stopsByCity = new Map();

    storedStops.forEach((stop) => {
      const key = normalizeCityValue(stop?.nome);
      const current = stopsByCity.get(key) || [];
      current.push(stop);
      stopsByCity.set(key, current);
    });

    const currentStopNames = [
      ...waypoints.map((item) => item.address),
      destination,
    ].filter((name) => String(name || "").trim());
    const selectionCities = mapOrderSelectionToStops(currentStopNames);
    const routeOrigin = String(origin || "").trim();

    return currentStopNames.map((name, index) => {
      const key = normalizeCityValue(name);
      const matches = stopsByCity.get(key) || [];
      const storedStop = matches.shift() || {};
      const selectionCity = selectionCities[index];
      stopsByCity.set(key, matches);

      return {
        ...storedStop,
        ordem: index + 1,
        nome: name,
        origemRota: routeOrigin,
        destinoRota: currentStopNames[currentStopNames.length - 1] || "",
        anterior: {
          nome: index === 0 ? routeOrigin : currentStopNames[index - 1],
        },
        proximo:
          currentStopNames[index + 1] !== undefined
            ? { nome: currentStopNames[index + 1] }
            : null,
        orderSelectionCity: selectionCity,
        concluida: Boolean(
          selectionCity?.salva ?? storedStop.concluida
        ),
        totalPedidosDisponiveis:
          Number(selectionCity?.totalPedidos) ||
          Number(storedStop.totalPedidosDisponiveis) ||
          0,
        totalPedidosSelecionados:
          selectionCity?.IDsPedidoSelecionados?.length || 0,
      };
    });
  }, [
    destination,
    macroStopsPayload,
    orderSelectionPayload,
    mapOrderSelectionToStops,
    origin,
    selectedRouteId,
    waypoints,
  ]);

  const macroStopsProgress = useMemo(() => {
    const completed = macroStops.filter((stop) => stop.concluida).length;

    return {
      totalCidades: macroStops.length,
      cidadesConcluidas: completed,
      percentual:
        macroStops.length > 0
          ? Math.round((completed / macroStops.length) * 100)
          : 0,
    };
  }, [macroStops]);

  useEffect(() => {
    if (!microPanelOpen || !selectedMicroStop?.nome) {
      return;
    }

    const currentStop = macroStops.find(
      (stop) =>
        normalizeCityValue(stop.nome) ===
        normalizeCityValue(selectedMicroStop.nome)
    );

    if (!currentStop) {
      setMicroPanelOpen(false);
      setSelectedMicroStopOrder(null);
      setSelectedMicroStop(null);
      setMicroMapPoints([]);
      setMicroPlanningDirty(false);
      return;
    }

    if (Number(currentStop.ordem) !== Number(selectedMicroStopOrder)) {
      setSelectedMicroStopOrder(currentStop.ordem);
      setSelectedMicroStop(currentStop);
    }
  }, [macroStops, microPanelOpen, selectedMicroStop, selectedMicroStopOrder]);

  const selectedOrderCity = useMemo(
    () =>
      macroStops.find(
        (stop) => Number(stop.ordem) === Number(selectedMicroStopOrder)
      )?.orderSelectionCity || null,
    [macroStops, selectedMicroStopOrder]
  );

  function handleOrderSelectionChange(nextSelectedOrderIds) {
    if (!selectedOrderCity) {
      return;
    }

    setOrderSelectionPayload((current) => {
      if (!current) {
        return current;
      }

      const targetIndex = selectedOrderCity.selectionIndex;
      const nextCities = (current.cidades || []).map((city, index) => {
        if (index !== targetIndex) {
          return city;
        }

        return {
          ...city,
          IDsPedidoSelecionados: nextSelectedOrderIds,
          salva: false,
        };
      });

      return {
        ...current,
        salva: false,
        cidades: nextCities,
      };
    });
    setOrderSelectionDirty(true);
    setOrderSelectionError("");
    setErrorMessage("");
    setSuccessMessage("");
  }

  return (
    <div className="route-page">
      <MenuLateral />

      <div className="route-content">
        <aside className="route-sidebar">
          <div className="route-header">
            <h2>Roteamento</h2>
            <p>Monte a rota, otimize a ordem e ajuste manualmente quando precisar.</p>
          </div>

          <div className="route-actions route-actions-top">
            <button
              type="button"
              className="primary"
              onClick={openAvailableOrderRoutesModal}
              disabled={
                loading ||
                reordering ||
                savingRoute ||
                openingRoute ||
                loadingAvailableOrderRoutes
              }
            >
              {loadingAvailableOrderRoutes ? "Buscando..." : "Buscar por rota"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={openRoutesModal}
              disabled={loading || reordering || savingRoute || openingRoute}
            >
              {openingRoute ? "Carregando..." : "Abrir rotas"}
            </button>
          </div>

          {operationalRouteId ? (
            <div className="route-operational-id" role="status">
              <span>
                {operationalRouteSource === "pedidos"
                  ? "Rota carregada dos pedidos"
                  : "Rota operacional"}
              </span>
              <strong>{operationalRouteId}</strong>
            </div>
          ) : null}

          <label className="route-field">
            <span>Nome da rota</span>
            <input
              type="text"
              value={routeName}
              maxLength={50}
              disabled={isRouteReadOnly}
              onChange={(event) => setRouteName(event.target.value)}
              placeholder="Ex.: Rota Centro"
            />
          </label>

          <label className="route-field">
            <span>Cidade origem</span>
            <input
              type="text"
              value={origin}
              disabled={isRouteReadOnly}
              onChange={(event) => {
                setOrigin(event.target.value);
                markOrderSelectionUnsaved();
              }}
              onBlur={(event) =>
                normalizeFieldValue(setOrigin, event.target.value)
              }
              list="ibge-city-options"
              placeholder="Ex.: Cuiaba, MT"
            />
          </label>

          <label className="route-field">
            <span>Cidade destino</span>
            <input
              type="text"
              value={destination}
              disabled={isRouteReadOnly}
              onChange={(event) => {
                setDestination(event.target.value);
                markOrderSelectionUnsaved();
              }}
              onBlur={(event) =>
                normalizeFieldValue(setDestination, event.target.value)
              }
              list="ibge-city-options"
              placeholder="Ex.: Rondonopolis, MT"
            />
          </label>

          <div className="route-field">
            <span>Cidade de parada</span>
            <div className="route-inline-field">
              <input
                type="text"
                value={cityInput}
                disabled={isRouteReadOnly}
                onChange={(event) => setCityInput(event.target.value)}
                onBlur={(event) =>
                  normalizeFieldValue(setCityInput, event.target.value)
                }
                list="ibge-city-options"
                placeholder="Adicionar cidade"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addWaypoint();
                  }
                }}
              />
              <button type="button" onClick={addWaypoint} disabled={isRouteReadOnly}>
                Adicionar
              </button>
            </div>
          </div>

          <datalist id="ibge-city-options">
            {cityResolver.options.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>

          <p className="route-helper-text">
            {loadingCityCatalog
              ? "Carregando cidades do IBGE..."
              : cityCatalogError
              ? cityCatalogError
              : `Validacao ativa com ${cityResolver.options.length} cidades do IBGE.`}
          </p>

          <div className="route-waypoints">
            <div className="route-section-title">
              <strong>Paradas</strong>
              <span>{waypoints.length} cidades</span>
            </div>

            <div className="route-waypoint-list">
              {waypoints.length === 0 ? (
                <p className="route-empty-state">
                  Nenhuma cidade adicionada ainda.
                </p>
              ) : (
                waypoints.map((item, index) => (
                  <div
                    key={item.id}
                    className={`route-waypoint-card${
                      draggedWaypointId === item.id ? " dragging" : ""
                    }${dragOverWaypointId === item.id ? " drag-over" : ""}`}
                    draggable={
                      !loading &&
                      !reordering &&
                      !isRoutingCooldownActive &&
                      !isRouteReadOnly
                    }
                    onDragStart={() => {
                      setDraggedWaypointId(item.id);
                      setDragOverWaypointId(item.id);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (draggedWaypointId && draggedWaypointId !== item.id) {
                        setDragOverWaypointId(item.id);
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      reorderWaypointByIds(draggedWaypointId, item.id);
                    }}
                    onDragEnd={() => {
                      setDraggedWaypointId(null);
                      setDragOverWaypointId(null);
                    }}
                  >
                    <button
                      type="button"
                      className="route-waypoint-order"
                      onDoubleClick={() => openOrderModal(item, index)}
                      disabled={
                        loading ||
                        reordering ||
                        isRoutingCooldownActive ||
                        isRouteReadOnly
                      }
                      aria-label={`Alterar posicao da cidade ${item.address}`}
                      title="Duplo clique para informar a nova posicao"
                    >
                      {index + 1}
                    </button>
                    <div className="route-waypoint-text">
                      <span className="route-waypoint-distance">
                        {waypointDistanceLabels[index] || "0 km"}
                      </span>
                      <strong>{item.address}</strong>
                    </div>
                    <div className="route-waypoint-actions">
                    <button
                      type="button"
                      className="drag-handle"
                      disabled={
                        loading ||
                        reordering ||
                        isRoutingCooldownActive ||
                        isRouteReadOnly
                      }
                      aria-label="Arrastar cidade"
                      title="Arraste para reordenar"
                    >
                      <FaGripVertical />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeWaypoint(item.id)}
                      disabled={loading || reordering || isRouteReadOnly}
                      aria-label="Remover cidade"
                      title="Remover"
                    >
                      <FaTimes />
                    </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="route-actions">
            <button
              type="button"
              className="primary"
              onClick={optimizeRoute}
              disabled={
                loading ||
                reordering ||
                savingRoute ||
                isRoutingCooldownActive ||
                isRouteReadOnly
              }
            >
              {loading
                ? "Roteando..."
                : isRoutingCooldownActive
                ? `Aguarde ${formatRoutingCooldown(routingCooldownRemaining)}`
                : "Rotear"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleSaveRoute}
              disabled={
                loading ||
                reordering ||
                savingRoute ||
                openingRoute ||
                loadingOrderSelection ||
                isRouteReadOnly
              }
            >
              {savingRoute
                ? "Salvando..."
                : selectedRouteId
                ? "Alterar"
                : "Salvar"}
            </button>
          </div>

          {loading ? (
            <div className="route-loading-panel" role="status" aria-live="polite">
              <div className="route-loading-header">
                <strong>Calculando a melhor rota</strong>
                <span className="route-loading-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
              <p>
                Estamos consultando os trechos e reorganizando a ordem das cidades.
              </p>
              <div className="route-loading-bar">
                <div className="route-loading-bar-fill" />
              </div>
            </div>
          ) : null}

          {isRoutingCooldownActive ? (
            <div className="route-cooldown-panel" role="alert" aria-live="assertive">
              <strong>Roteamento temporariamente pausado</strong>
              <p>{routingCooldown.message}</p>
              <span>
                Nova tentativa em {formatRoutingCooldown(routingCooldownRemaining)}
                {routingRetryAtLabel ? ` — liberação prevista: ${routingRetryAtLabel}` : ""}.
              </span>
            </div>
          ) : null}

          <div className="route-actions">
            <button
              type="button"
              className="warning"
              onClick={handleExportRouteJson}
              disabled={
                loading ||
                reordering ||
                savingRoute ||
                openingRoute ||
                exportingRouteJson ||
                !selectedRouteId ||
                isRouteReadOnly
              }
            >
              {exportingRouteJson
                ? "Exportando e finalizando..."
                : isRouteReadOnly
                ? "Rota finalizada"
                : "Finalizar rota"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleExportRoute}
              disabled={loading || reordering || savingRoute}
            >
              Gerar PDF/Excel
            </button>
          </div>

          <p className="route-helper-text"></p>

          {errorMessage ? <p className="route-error">{errorMessage}</p> : null}
          {successMessage ? <p className="route-success">{successMessage}</p> : null}

          <div className="route-summary">
            <div className="route-summary-card">
              <span>Distancia total</span>
              <strong>{formatDistance(totalDistance)}</strong>
            </div>
            <div className="route-summary-card">
              <span>Tempo estimado</span>
              <strong>{formatDuration(totalDuration)}</strong>
            </div>
          </div>

          <div className="route-micro-entry">
            <div className="route-section-title">
              <strong>Planejamento de entregas</strong>
              <span>
                {`${macroStopsProgress.cidadesConcluidas}/${macroStopsProgress.totalCidades} cidades`}
              </span>
            </div>

            {macroStops.length > 0 ? (
              <div className="route-micro-entry-progress">
                <span
                  style={{
                    width: `${macroStopsProgress.percentual}%`,
                  }}
                />
                <small>{macroStopsProgress.percentual}% concluido</small>
              </div>
            ) : null}

            {orderSelectionDirty && macroStops.length > 0 ? (
              <p className="route-order-selection-warning">
                A selecao foi alterada. Clique em {selectedRouteId ? "Alterar" : "Salvar"}{" "}
                antes de finalizar a rota.
              </p>
            ) : null}

            {!selectedRouteId && !orderSelectionPayload ? (
              <p className="route-empty-state">
                Carregue uma rota de pedidos para selecionar uma cidade.
              </p>
            ) : null}

            {loadingMacroStops || loadingOrderSelection ? (
              <p className="route-empty-state">Carregando cidades...</p>
            ) : null}

            {macroStopsError || orderSelectionError ? (
              <p className="route-error">
                {orderSelectionError || macroStopsError}
              </p>
            ) : null}

            {(selectedRouteId || orderSelectionPayload) &&
            !loadingMacroStops &&
            !loadingOrderSelection &&
            !macroStopsError &&
            !orderSelectionError &&
            macroStops.length === 0 ? (
              <p className="route-empty-state">
                Nenhuma cidade disponivel para selecao de pedidos.
              </p>
            ) : null}

            {macroStops.length > 0 ? (
              <div className="route-micro-entry-list">
                {macroStops.map((stop) => (
                  <button
                    key={stop.ordem}
                    type="button"
                    className={`route-micro-entry-button${
                      selectedMicroStopOrder === stop.ordem && microPanelOpen
                        ? " active"
                        : ""
                    }`}
                    onClick={() => openMicroPanelForStop(stop)}
                    disabled={isRouteReadOnly}
                  >
                    <strong>
                      {stop.ordem}. {stop.nome}
                    </strong>
                    <span className="route-micro-entry-meta">
                      <span
                        className={`route-micro-entry-status${
                          stop.concluida ? " completed" : " pending"
                        }`}
                        aria-label={stop.concluida ? "Concluida" : "Pendente"}
                        title={stop.concluida ? "Concluida" : "Pendente"}
                      >
                        {stop.concluida ? <FaCheckCircle /> : <FaClock />}
                      </span>
                      <span>
                        {stop.totalPedidosSelecionados || 0} de{" "}
                        {stop.totalPedidosDisponiveis || 0} pedido(s) selecionado(s)
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </aside>

        {microPanelOpen ? (
          <aside className="route-micro-sidebar">
            {selectedOrderCity ? (
              <OrderSelectionPanel
                routeName={routeName}
                citySelection={selectedOrderCity}
                onClose={() => closeMicroPanel(true)}
                onSelectionChange={handleOrderSelectionChange}
                onMapPointsChange={handleMicroMapPointsChange}
                readOnly={isRouteReadOnly}
              />
            ) : (
              <MicroPlanningPanel
                routeId={selectedRouteId}
                routeName={routeName}
                selectedStopOrder={selectedMicroStopOrder}
                onClose={closeMicroPanel}
                onMapPointsChange={handleMicroMapPointsChange}
                onDirtyChange={setMicroPlanningDirty}
                onStopsPayloadChange={setMacroStopsPayload}
              />
            )}
          </aside>
        ) : null}

        <section className="route-map-panel">
          <MapContainer
            center={mapCenter}
            zoom={5}
            style={{ height: "100vh", width: "100%" }}
          >
            <RouteMapController focusTarget={mapFocusTarget} />

            <TileLayer
              attribution="<p>GRUPO PALMARES</p>"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {routePolyline.length > 1 ? (
              <Polyline
                positions={routePolyline}
                pathOptions={{ color: "#1d64d8", weight: 5, opacity: 0.85 }}
              />
            ) : null}

            {routePolyline.length <= 1 && pointConnectionLine.length > 1 ? (
              <Polyline
                positions={pointConnectionLine}
                pathOptions={{ color: "#1d64d8", weight: 4, opacity: 0.9 }}
              />
            ) : null}

            {routePoints.map((point, index) => {
              if (!hasValidCoordinates(point)) {
                return null;
              }

              const markerType =
                index === 0
                  ? "origin"
                  : index === routePoints.length - 1
                  ? "destination"
                  : "waypoint";

              return (
                <Marker
                  key={`${point.address}-${index}`}
                  position={[point.coordinates.lat, point.coordinates.lng]}
                  icon={createOrderIcon(index, markerType)}
                >
                  <Popup>
                    <strong>{point.address}</strong>
                    <br />
                    {markerType === "waypoint" ? `Parada ${index}` : null}
                    {markerType === "waypoint" ? <br /> : null}
                    Trecho anterior: {formatDistance(point.distance_from_previous)}
                  </Popup>
                </Marker>
              );
            })}

            {fallbackSegmentLabels.map((segment) =>
              segment ? (
                <Marker
                  key={segment.id}
                  position={segment.position}
                  icon={createSegmentIcon(segment.label)}
                />
              ) : null
            )}

            {microMapPoints.map((point) => {
              const [latitude, longitude] = Array.isArray(point.position)
                ? point.position
                : [];

              if (!isValidLatLng(latitude, longitude)) {
                return null;
              }

              return (
                <Marker
                  key={point.id}
                  position={point.position}
                  icon={createMicroClientIcon()}
                >
                  <Popup>
                    <strong>{point.title}</strong>
                    <br />
                    {point.subtitle}
                    <br />
                    {point.orders} pedido(s)
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </section>
      </div>

      {showRouteModal ? (
        <div className="route-modal-overlay">
          <div className="route-modal">
            <div className="route-modal-header">
              <h3>Selecionar rota</h3>
              <button
                type="button"
                className="route-modal-close"
                onClick={() => setShowRouteModal(false)}
              >
                x
              </button>
            </div>

            <div className="route-modal-filters">
              <button
                type="button"
                className={modalStatusFilter === 0 ? "active" : ""}
                onClick={() => changeModalFilter(0)}
                disabled={modalLoading}
              >
                Abertas
              </button>
              <button
                type="button"
                className={modalStatusFilter === 1 ? "active" : ""}
                onClick={() => changeModalFilter(1)}
                disabled={modalLoading}
              >
                Fechadas (ultimas 30)
              </button>
            </div>

            <div className="route-modal-list">
              {modalLoading ? <p>Carregando rotas...</p> : null}
              {!modalLoading && modalError ? <p>{modalError}</p> : null}
              {!modalLoading && !modalError && availableRoutes.length === 0 ? (
                <p>Nenhuma rota encontrada para o filtro selecionado.</p>
              ) : null}

              {!modalLoading && !modalError
                ? availableRoutes.map((item) => (
                    <button
                      key={item.IDrota}
                      type="button"
                      className="route-modal-item"
                      onClick={() => handleSelectRoute(item)}
                      disabled={openingRoute}
                    >
                      <div>
                        <strong>{item.nomerota || `Rota ${item.IDrota}`}</strong>
                        <span>ID: {item.IDrota}</span>
                      </div>
                      <small>
                        Alteracao:{" "}
                        {formatDateTime(
                          item.dataalteracao ||
                            item["dataalteração"] ||
                            item.datacriacao
                        )}
                      </small>
                    </button>
                  ))
                : null}
            </div>
          </div>
        </div>
      ) : null}

      {showAvailableOrderRoutesModal ? (
        <div
          className="route-modal-overlay"
          onClick={() => setShowAvailableOrderRoutesModal(false)}
        >
          <div className="route-modal" onClick={(event) => event.stopPropagation()}>
            <div className="route-modal-header">
              <h3>Buscar por rota</h3>
              <button
                type="button"
                className="route-modal-close"
                onClick={() => setShowAvailableOrderRoutesModal(false)}
              >
                x
              </button>
            </div>

            <p className="route-modal-description">
              Rotas dos pedidos disponiveis. Selecione uma para carregar todas as
              cidades na lista de paradas.
            </p>

            {!loadingAvailableOrderRoutes && availableOrderRoutesError ? (
              <p className="route-modal-feedback route-error" role="alert">
                {availableOrderRoutesError}
              </p>
            ) : null}

            <div className="route-modal-list">
              {loadingAvailableOrderRoutes ? (
                <p>Carregando rotas disponiveis...</p>
              ) : null}
              {!loadingAvailableOrderRoutes &&
              !availableOrderRoutesError &&
              availableOrderRoutes.length === 0 ? (
                <p>Nenhuma rota possui pedidos disponiveis.</p>
              ) : null}

              {!loadingAvailableOrderRoutes
                ? availableOrderRoutes.map((item) => (
                    <button
                      key={item.IDrota}
                      type="button"
                      className="route-modal-item"
                      onClick={() => handleSelectAvailableOrderRoute(item)}
                    >
                      <div>
                        <strong>Rota {item.IDrota}</strong>
                        <span>
                          {item.totalCidades} cidade(s) · {item.totalPedidos}{" "}
                          pedido(s)
                        </span>
                      </div>
                      <small>
                        {(item.cidades || [])
                          .map((city) => `${city.cidade} - ${city.uf}`)
                          .join(", ")}
                      </small>
                    </button>
                  ))
                : null}
            </div>
          </div>
        </div>
      ) : null}

      {showOrderModal ? (
        <div className="route-modal-overlay" onClick={closeOrderModal}>
          <div className="route-modal route-order-modal" onClick={(event) => event.stopPropagation()}>
            <div className="route-modal-header">
              <h3>Alterar posicao</h3>
              <button
                type="button"
                className="route-modal-close"
                onClick={closeOrderModal}
              >
                x
              </button>
            </div>

            <form className="route-order-form" onSubmit={handleOrderSubmit}>
              <p className="route-order-description">
                {selectedWaypointForOrder?.address || "Cidade selecionada"}
              </p>

              <label className="route-field">
                <span>Nova ordem</span>
                <input
                  type="number"
                  min="1"
                  max={waypoints.length}
                  value={orderInput}
                  onChange={(event) => setOrderInput(event.target.value)}
                  placeholder={`Entre 1 e ${waypoints.length}`}
                  autoFocus
                />
              </label>

              <p className="route-helper-text">
                Digite uma posicao entre 1 e {waypoints.length}.
              </p>

              <div className="route-actions route-order-actions">
                <button type="button" className="secondary" onClick={closeOrderModal}>
                  Cancelar
                </button>
                <button type="submit" className="primary" disabled={loading || reordering}>
                  Mover
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Roteamento;
